import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { toast } from "react-toastify";
import { apiputFormData, apigetPublic } from "../../service/api";

const BranchEditModal = ({ open, onClose, branchData, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: "",
    slug: "",

    // Address
    address: "",
    city: "",
    stateCode: "",
    zip: "",

    // Image
    branchImageUrl: "",

    // Contact
    phone: "",
    email: "",
    nmls: "",
    preQualificationLink: "",
    // Socials
    facebook: "",
    instagram: "",
    linkedin: "",
    tiktok: "",
    twitter: "",

    // Relations
    loanOfficers: [], // array of LO IDs
  });

  const [branchImageFile, setBranchImageFile] = useState(null);
  const [loanOfficerList, setLoanOfficerList] = useState([]);

  // -------------------------------
  // Load initial form data
  // -------------------------------
  useEffect(() => {
    if (!branchData) return;

    setFormData({
      name: branchData.name || "",
      slug: branchData.slug || "",

      address: branchData.address || "",
      city: branchData.city || "",
      stateCode: (branchData.stateCode || "").toUpperCase(),
      zip: branchData.zip || "",

      branchImageUrl: branchData.branchImageUrl || "",

      phone: branchData.phone || "",
      email: branchData.email || "",
      nmls: branchData.nmls || "",
      preQualificationLink: branchData.preQualificationLink || "",
      facebook: branchData.facebook || "",
      instagram: branchData.instagram || "",
      linkedin: branchData.linkedin || "",
      tiktok: branchData.tiktok || "",
      twitter: branchData.twitter || "",

      loanOfficers: Array.isArray(branchData.loanOfficers)
        ? branchData.loanOfficers.map((lo) => lo?._id).filter(Boolean)
        : [],
    });

    setBranchImageFile(null);
  }, [branchData]);

  // -------------------------------
  // Load loan officers list
  // -------------------------------
  useEffect(() => {
    const loadLoanOfficers = async () => {
      const res = await apigetPublic("loanofficer/search?location=");
      if (res?.status === 200 && Array.isArray(res.data.result)) {
        setLoanOfficerList(res.data.result);
      }
    };
    loadLoanOfficers();
  }, []);

  if (!branchData) return null;

  // -------------------------------
  // Handlers
  // -------------------------------
  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    // keep stateCode uppercase
    if (name === "stateCode") {
      setFormData((prev) => ({ ...prev, stateCode: value.toUpperCase() }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLoanOfficerChange = (loId, checked) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.loanOfficers) ? prev.loanOfficers : [];
      const next = checked
        ? Array.from(new Set([...current, loId]))
        : current.filter((id) => id !== loId);
      return { ...prev, loanOfficers: next };
    });
  };

  // -------------------------------
  // Submit PUT
  // -------------------------------
  const handleSubmit = async () => {
    try {
      const data = new FormData();

      // Required + core fields
      data.append("name", formData.name);
      data.append("slug", formData.slug);
      data.append("address", formData.address);
      data.append("city", formData.city);
      data.append("stateCode", (formData.stateCode || "").toUpperCase());
      data.append("zip", formData.zip);

      // Contact
      data.append("phone", formData.phone);
      data.append("email", formData.email);
      data.append("nmls", formData.nmls);
      data.append("preQualificationLink", formData.preQualificationLink);
      // Socials
      data.append("facebook", formData.facebook);
      data.append("instagram", formData.instagram);
      data.append("linkedin", formData.linkedin);
      data.append("tiktok", formData.tiktok);
      data.append("twitter", formData.twitter);

      // Loan officers (as array indices)
      (formData.loanOfficers || []).forEach((id, idx) =>
        data.append(`loanOfficers[${idx}]`, id)
      );

      // Upload image if changed
      if (branchImageFile) {
        data.append("branchImage", branchImageFile);
      }

      const result = await apiputFormData(`branches/${branchData._id}`, data);

      if (result?.status === 200) {
        toast.success("Branch updated successfully!");
        onUpdate?.();
        onClose?.();
      } else {
        toast.error("Failed to update branch.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Unexpected error while updating the branch.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Branch</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="name"
              label="Branch Name"
              value={formData.name}
              onChange={handleFieldChange}
            />
          </Grid>

          {/* Slug */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="slug"
              label="Slug"
              value={formData.slug}
              onChange={handleFieldChange}
              helperText="Unique URL slug (ex: dallas-tx)"
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="address"
              label="Address"
              value={formData.address}
              onChange={handleFieldChange}
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="city"
              label="City"
              value={formData.city}
              onChange={handleFieldChange}
            />
          </Grid>

          {/* State */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="stateCode"
              label="State Code (ex: FL)"
              value={formData.stateCode}
              onChange={handleFieldChange}
              inputProps={{ maxLength: 2 }}
            />
          </Grid>

          {/* Zip */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="zip"
              label="Zip"
              value={formData.zip}
              onChange={handleFieldChange}
            />
          </Grid>

          {/* Contact */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Contact
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="phone"
              label="Phone"
              value={formData.phone}
              onChange={handleFieldChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="email"
              label="Email"
              value={formData.email}
              onChange={handleFieldChange}
            />
          </Grid>
          
  <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="nmls"
              label="NMLS #"
              value={formData.nmls}
              onChange={handleFieldChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              name="preQualificationLink"
              label="Pre-Qualification Link"
              value={formData.preQualificationLink}
              onChange={handleFieldChange}
              helperText="URL for the Get Pre-Qualified button on the branch page"
            />
          </Grid>

          {/* Socials */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Social Links
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="facebook"
              label="Facebook URL"
              value={formData.facebook}
              onChange={handleFieldChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="instagram"
              label="Instagram URL"
              value={formData.instagram}
              onChange={handleFieldChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="linkedin"
              label="LinkedIn URL"
              value={formData.linkedin}
              onChange={handleFieldChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="tiktok"
              label="TikTok URL"
              value={formData.tiktok}
              onChange={handleFieldChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="twitter"
              label="X / Twitter URL"
              value={formData.twitter}
              onChange={handleFieldChange}
            />
          </Grid>

          {/* Image */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
              Branch Image
            </Typography>

            {formData.branchImageUrl && (
              <img
                src={formData.branchImageUrl}
                alt="Branch"
                style={{
                  width: 180,
                  height: 120,
                  objectFit: "cover",
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              />
            )}

            <Typography variant="subtitle2">Upload New Image</Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setBranchImageFile(e.target.files?.[0] || null)}
            />
          </Grid>

          {/* Loan Officer List */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
              Loan Officers Assigned to This Branch
            </Typography>

            {loanOfficerList.map((lo) => {
              const checked = formData.loanOfficers.includes(lo._id);
              const id = `lo-${lo._id}`;
              return (
                <Box key={lo._id} sx={{ mb: 1 }}>
                  <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      handleLoanOfficerChange(lo._id, e.target.checked)
                    }
                  />
                  <label
                    htmlFor={id}
                    style={{ marginLeft: 8, cursor: "pointer" }}
                  >
                    {lo.firstName} {lo.lastName}
                    {lo.city && lo.state ? ` — ${lo.city}, ${lo.state}` : ""}
                  </label>
                </Box>
              );
            })}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchEditModal;
